import { apiPost } from "@/shared/api";
import type { Order } from "./types";

/** Ordini Cosma nella selezione: gate del bottone "Invia a Cosma". Il backend
 *  marca ``fornitore.kind === 'cosma'`` (apply_cosma_kind: SKU nel catalogo
 *  Cosma E fornitore altrimenti unknown — rispetta AS-wins e Itamoby/Ityhome
 *  autoritativi). Il tasto agisce SOLO sugli ordini Cosma. */
export function ordersCosma<T extends Pick<Order, "fornitore">>(orders: T[]): T[] {
  return orders.filter((o) => o.fornitore.kind === "cosma");
}

export type CosmaSendBulkItem = {
  order_id: number;
  reference: string;
};

export type CosmaSendBulkSkipped = {
  order_id: number | null;
  reference: string;
  reason: string;
};

export type CosmaSendBulkError = {
  order_id: string;
  message: string;
};

export type CosmaSendBulkResult = {
  requested: number;
  uploaded: CosmaSendBulkItem[];
  skipped: CosmaSendBulkSkipped[];
  errors: CosmaSendBulkError[];
};

export type CosmaPackage = { kg: number; l: number; a: number; p: number };

export type CosmaPreviewOrder = {
  order_id: number;
  reference: string;
  shipping: {
    customer_name: string;
    address: string;
    postcode: string;
    city: string;
    province: string;
    country: string;
    phone: string;
  };
  items: Array<{ sku: string; title: string; quantity: number }>;
  /** Colli proposti: salvati sull'ordine o default 1-per-unità da catalogo. */
  packages: CosmaPackage[];
  /** Baseline per-unità dal catalogo: base del ricalcolo stepper. */
  unit_packages: CosmaPackage[];
  packages_source: "saved" | "catalog";
  already_sent: boolean;
  warnings: string[];
};

export type CosmaPreviewResult = {
  orders: CosmaPreviewOrder[];
  errors: CosmaSendBulkError[];
};

/** Anteprima invio Cosma per il popup di conferma: dati spedizione, righe e
 *  colli proposti per ordine. Solo DB lato server, nessuna connessione FTP. */
export function fetchCosmaPreview(orderIds: string[]): Promise<CosmaPreviewResult> {
  return apiPost<CosmaPreviewResult>("/api/suppliers/cosma/orders/preview", {
    order_ids: orderIds,
  });
}

/** Invia gli ordini selezionati a Cosma. Per OGNI ordine il backend genera
 *  (o riusa) la LDV e la carica in ``/Ldv/<ordine>_1.pdf``, poi un unico CSV
 *  in ``/Ordini/``. ``packagesOverrides`` (popup conferma, chiave=reference):
 *  colli editati → persistiti su Order.package_dimensions + LDV rigenerata
 *  per quegli ordini. Dedup salvo ``force``; AS-wins lato server. */
export function sendCosmaOrdersBulk(
  orderIds: string[],
  force = false,
  packagesOverrides: Record<string, CosmaPackage[]> = {},
): Promise<CosmaSendBulkResult> {
  return apiPost<CosmaSendBulkResult>("/api/suppliers/cosma/orders/send-bulk", {
    order_ids: orderIds,
    force,
    packages_overrides: packagesOverrides,
  });
}
